import { Platform } from 'react-native';
import { isNil } from 'es-toolkit';
import numeral from 'numeral';

import type { AccountSubtypeName } from '@savvy/constants/generated/gql-enums';
import type {
  AssetClassName,
  AssetSubclassName,
  PerformanceTimePeriodName,
} from '@savvy/constants/generated/gql-types';
import type { DateRangeOption, DisconnectedLinkedAccountStatus } from '@savvy/deprecated-shared/types/portfolio';
import { ASSET_CATEGORY, ASSET_CATEGORY_COLOR_MAP } from '@savvy/deprecated-shared/constants/portfolio';
import { DISCONNECTED_LINKED_ACCOUNT_STATUSES } from '@savvy/deprecated-shared/constants/portfolio/account-consts';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { assetCategoryColors } from '@savvy/deprecated-shared/theme';
import defaultGuard from '@savvy/utils/default-guard';
import { hasKey } from '@savvy/utils/typescript';

import { snakeCaseToTitleCase, toTitleCase } from './formatting';

export function getAssetCategoryColor(assetGrouping: string | null) {
  if (isNil(assetGrouping) || !hasKey(ASSET_CATEGORY_COLOR_MAP, assetGrouping)) {
    return assetCategoryColors.equity;
  }
  switch (assetGrouping) {
    case ASSET_CATEGORY.EQUITY:
      return assetCategoryColors.equity;
    case ASSET_CATEGORY.FIXED_INCOME:
      return assetCategoryColors.fixedIncome;
    case ASSET_CATEGORY.ALTERNATIVES:
      return assetCategoryColors.alternatives;
    case ASSET_CATEGORY.CASH:
      return assetCategoryColors.cash;
    case ASSET_CATEGORY.CASH_AND_CASH_EQUIVALENTS:
      return assetCategoryColors.cash;
    case ASSET_CATEGORY.MULTI_ASSET_STRATEGY:
      return assetCategoryColors.multiAssetStrategy;
    default:
      return defaultGuard(assetGrouping, assetCategoryColors.equity);
  }
}

/**
 * Check if the account is a disconnected linked account. For example, if an
 * account has been disconnected from Plaid, this will return true.
 * @param sourceStatus The Account > ProviderAccount > sourceStatus field
 * @returns Whether the account is a disconnected linked account
 */
export function isDisconnectedLinkedAccountSourceStatus(sourceStatus: string | null | undefined) {
  return (
    !isNil(sourceStatus) &&
    // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
    DISCONNECTED_LINKED_ACCOUNT_STATUSES.includes(sourceStatus as DisconnectedLinkedAccountStatus)
  );
}

/**
 * Format a number or string as a USD currency value
 * @param value The value to format
 * @returns The formatted value
 */
export function formatItemValue(value: number | string) {
  return numeral(value).format('$0,0.00');
}

export function accountSubtypeDisplayName(accountSubtypeName: AccountSubtypeName | null | undefined): string {
  // TODO: Can simplify this by having all of the title-cased strings grouped together
  switch (accountSubtypeName) {
    case 'beneficiary_ira':
      return 'Beneficiary IRA';
    case 'cd':
      return 'Certificate of Deposit';
    case 'company_daf':
      return 'Company DAF';
    case 'contributory_ira':
      return 'Contributory IRA';
    case 'education_ira':
      return 'Education IRA';
    case 'hsa':
      return 'Health Savings Account';
    case 'plan401a':
      return '401a';
    case 'plan401k':
      return '401k';
    case 'plan403b':
      return '403b';
    case 'plan457b':
      return '457b';
    case 'plan529':
      return '529';
    case 'cash_isa':
      return 'Cash ISA';
    case 'gst_exempt':
      return 'GST Exempt';
    case 'ira_rollover':
      return 'IRA Rollover';
    case 'inherited_ira':
      return 'Inherited IRA';
    case 'lif':
      return 'Life Income Fund (LIF)';
    case 'non_taxable_brokerage_account':
      return 'Non-taxable Brokerage Account';
    case 'roth':
      return 'Roth IRA';
    case 'roth_401k':
      return 'Roth 401k';
    case 'sep_ira':
      return 'SEP IRA';
    case 'simple_ira':
      return 'Simple IRA';
    case 'uniform_transfers_to_minors_account':
      return 'Uniform Transfers to Minors Account';
    case 'non_profit':
      return 'Non-Profit';
    case 'gic':
    case 'ebt':
    case 'ira':
    case 'isa':
    case 'jtwros':
    case 'lira':
    case 'lrif':
    case 'lrsp':
    case 'prif':
    case 'qshr':
    case 'rdsp':
    case 'resp':
    case 'rlif':
    case 'rrif':
    case 'rrsp':
    case 'sarsep':
    case 'sipp':
    case 'tfsa':
    case 'ugma':
    case 'utma':
    case 'iso':
    case 'nso':
    case 'rsa':
    case 'rsu':
      return accountSubtypeName.toUpperCase();
    case 'cash_management':
    case 'checking':
    case 'corporation':
    case 'defined_benefit_plan':
    case 'money_market':
    case 'paypal':
    case 'pledged_asset_line':
    case 'prepaid':
    case 'savings':
    case 'credit_card':
    case 'paypal_credit_card':
    case 'auto':
    case 'business':
    case 'commercial':
    case 'construction':
    case 'consumer':
    case 'home_equity':
    case 'line_of_credit':
    case 'loan':
    case 'mortgage':
    case 'other':
    case 'overdraft':
    case 'student':
    case 'brokerage':
    case 'contributory_roth':
    case 'foundation':
    case 'education_savings_account':
    case 'fixed_annuity':
    case 'health_reimbursement_arrangement':
    case 'joint':
    case 'joint_tenant':
    case 'keogh':
    case 'life_insurance':
    case 'marital_trust':
    case 'mutual_fund':
    case 'other_annuity':
    case 'other_insurance':
    case 'pension':
    case 'personal_choice_retirement_account':
    case 'private_equity':
    case 'profit_sharing_plan':
    case 'retirement':
    case 'revocable_trust':
    case 'stock_plan':
    case 'trust':
    case 'variable_annuity':
    case 'hot_wallet':
    case 'cold_wallet':
    case 'hardware_wallet':
    case 'exchange':
    case 'private':
    case 'partnership':
    case 'public':
    case 'real_estate':
    case 'spouse':
    case 'transfer_on_death':
    case 'community_property':
    case 'conservatorship':
    case 'conversion_roth':
    case 'custodial':
    case 'defined_contribution_plan':
    case 'defined_pension':
    case 'estate':
    case 'guardianship':
    case 'invalid_type':
    case 'money_purchase_pension_plan':
    case 'optional_retirement_program':
    case 'retirement_plan':
    case 'simplified_employee_pension_plan':
    case 'sole_and_separate_property':
    case 'tax_sheltered_account':
    case 'non_custodial_wallet':
    case 'annuity':
    case 'corporate_401k':
    case 'custodial_roth_contributory_ira':
    case 'defined':
    case 'donor_advised_fund':
    case 'educational_savings_account':
    case 'financial_planning_fee':
    case 'health_savings_account':
    case 'individual':
    case 'inherited_roth_ira':
    case 'ira_cont':
    case 'roth_ira':
    case 'simple':
    case 'uniform_gift_to_minors_account':
    case 'tenants_in_common':
    case 'tenants_by_entirety':
    case 'trust_under_will':
      return snakeCaseToTitleCase(accountSubtypeName);
    case undefined:
    case null:
      return 'Other';
    default:
      return defaultGuard(accountSubtypeName, toTitleCase(accountSubtypeName));
  }
}

export function formatDecimalAsPercent(change: number | string) {
  return numeral(change).format('0,0.00%');
}

export function formatPercentChangeWithArrow(change: number | string) {
  const baseStr = formatDecimalAsPercent(change);
  const changeNum = typeof change === 'string' ? Number.parseFloat(change) : change;
  if (baseStr.includes('NaN')) {
    return 'N/A';
  } else if (
    baseStr === '0.00%' ||
    // The unicode arrow characters aren't supported by default on iOS and
    // Android, so we'll just return the base string.
    Platform.OS !== 'web'
  ) {
    return baseStr;
  } else if (changeNum > 0) {
    return `↗${baseStr}`;
  } else if (changeNum < 0) {
    return `↘${baseStr}`;
  }
  return baseStr;
}

export function mapDateRangeOptionToPerformanceTimePeriodName(
  dateRangeOption: DateRangeOption,
): PerformanceTimePeriodName {
  switch (dateRangeOption) {
    case '1M':
      return 'past_month';
    case '3M':
      return 'past_three_months';
    case '1Y':
      return 'past_year';
    case 'YTD':
      return 'year_to_date';
    case 'ALL':
      return 'all_time';
    default:
      return defaultGuard(dateRangeOption, 'all_time');
  }
}

export function performanceFromQuickDate<T extends { timePeriod?: PerformanceTimePeriodName | null }>({
  performanceOverTime,
  quickDate,
}: {
  performanceOverTime: T[] | null | undefined;
  quickDate: PerformanceTimePeriodName;
}): T | undefined {
  return (
    performanceOverTime?.find((performance) => performance.timePeriod === quickDate) ||
    // Past Year lookback does not return unless >= 1 year of history
    performanceOverTime?.find((performance) => performance.timePeriod === 'all_time')
  );
}

type ChangeColorOptions = {
  na: string;
  negative: string;
  neutral: string;
  positive: string;
};

export function formatChangeTypography(
  change: number,
  { na, negative, neutral, positive }: ChangeColorOptions,
): string {
  const formattedChange = formatDecimalAsPercent(change);
  if (formattedChange.includes('NaN')) {
    return na;
  } else if (formattedChange === '0.00%') {
    return neutral;
  } else if (change > 0) {
    return positive;
  }
  return negative;
}

// Spread by this package's x-pages (client-preset). apps/web can no longer spread it:
// its codegen glob is rooted at apps/web/src and client-preset rejects a fragment
// it cannot find in its own document set — hence the structural param below.
export const AssetSubtitleFragment = tadaGraphql(/* GraphQL */ `
  fragment AssetSubtitle on Asset {
    assetClass
    assetSubclass
    companyEquityGrantType
  }
`);

export type AssetSubtitleData = {
  assetClass: AssetClassName;
  assetSubclass: AssetSubclassName | null;
  companyEquityGrantType: string | null;
};

export function assetSubtitle(asset: AssetSubtitleData) {
  if (asset.assetClass === 'company_equity') {
    if (asset.companyEquityGrantType) {
      return `${asset.companyEquityGrantType} grant`;
    }
  }
  if (asset.assetSubclass) {
    return assetSubclassDisplayName(asset.assetSubclass);
  }
  return assetClassDisplayName(asset.assetClass);
}

export function assetSubclassDisplayName(assetSubclass: AssetSubclassName | null) {
  switch (assetSubclass) {
    case 'antique':
    case 'art':
    case 'boat':
    case 'car':
    case 'jewelry':
    case 'other':
    case 'residence':
    case 'investment_property':
    case 'primary_residence':
    case 'secondary_residence':
      return snakeCaseToTitleCase(assetSubclass);
    case 'non_residence':
      return 'Non-Residence';
    case null:
      return 'Other';
    default:
      return defaultGuard(assetSubclass, snakeCaseToTitleCase(assetSubclass));
  }
}

export function assetClassDisplayName(assetClass: AssetClassName) {
  switch (assetClass) {
    case 'collectible':
    case 'company_equity':
    case 'insurance_policy':
    case 'other':
    case 'real_estate':
    case 'vehicle':
      return snakeCaseToTitleCase(assetClass);
    default:
      return defaultGuard(assetClass, snakeCaseToTitleCase(assetClass));
  }
}
